import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { IChangeReview, isFieldDiff } from "../../../../types/review";
import ImageDiff from "../../../../components/diff/ImageDiff";

type Props = {
  open: boolean;
  onClose: () => void;
  review: IChangeReview;
  onAccept: () => Promise<void> | void;
  onReject: () => Promise<void> | void;
};

type FlatEntry = {
  field: string;
  current: string | null;
  proposed: string;
};

// Maps backend service keys to washingClass translation keys.
const SERVICE_KEY_MAP: Record<string, string> = {
  inner: "innerWash",
  outer: "outerWash",
  both: "fullWash",
};

// Converts a flat dot-notation field path to a human-readable label.
const formatFieldLabel = (field: string, t: (key: string) => string): string => {
  const parts = field.split(".");

  // services.sedan.inner → "Sedan — Inner Wash"
  if (parts[0] === "services" && parts.length === 3) {
    const vehicle = t(`settings.washingClass.${parts[1]}`);
    const svcKey = SERVICE_KEY_MAP[parts[2]] ?? parts[2];
    const svc = t(`settings.washingClass.${svcKey}`);
    return `${vehicle} — ${svc}`;
  }

  return parts
    .map((p) => p.replace(/([A-Z])/g, " $1").trim())
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" › ");
};

// Render a leaf value as plain text. Never dangerouslySetInnerHTML (XSS — T-28-04-03).
const asText = (value: any): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

// Flatten changes to table rows. Skip the top-level "images" key — shown in slider.
function flattenChanges(
  changes: Record<string, any> | undefined,
  prefix = ""
): FlatEntry[] {
  const out: FlatEntry[] = [];
  if (!changes || typeof changes !== "object") return out;

  Object.keys(changes).forEach((key) => {
    if (!prefix && key === "images") return; // rendered separately in slider
    const value = changes[key];
    const label = prefix ? `${prefix}.${key}` : key;

    if (isFieldDiff(value)) {
      out.push({
        field: label,
        current: asText((value as any).old),
        proposed: asText((value as any).new),
      });
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      out.push(...flattenChanges(value, label));
    } else {
      out.push({
        field: label,
        current: null,
        proposed: asText(value),
      });
    }
  });

  return out;
}

// ── Image slider ────────────────────────────────────────────────────────────
const ImageSlider = ({ images }: { images: string[] }) => {
  const { t } = useTranslation();
  const [idx, setIdx] = React.useState(0);

  if (!images.length) return null;

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {t("carWashDetails.images")} ({images.length})
      </Typography>

      {/* Main image */}
      <Box sx={{ position: "relative", bgcolor: "grey.100", borderRadius: 1, overflow: "hidden", textAlign: "center" }}>
        <Box
          component="img"
          src={images[idx]}
          alt={t("carWashes.imageAltIndexed", { index: idx + 1 })}
          sx={{ width: "100%", maxHeight: 320, objectFit: "contain", display: "block" }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              size="small"
              disabled={idx === 0}
              onClick={() => setIdx((i) => i - 1)}
              sx={{
                position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.45)", color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.15)", color: "rgba(255,255,255,0.4)" },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              size="small"
              disabled={idx === images.length - 1}
              onClick={() => setIdx((i) => i + 1)}
              sx={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.45)", color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.15)", color: "rgba(255,255,255,0.4)" },
              }}
            >
              <ChevronRight />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                position: "absolute", bottom: 6, right: 10,
                bgcolor: "rgba(0,0,0,0.5)", color: "white",
                px: 1, py: 0.25, borderRadius: 1,
              }}
            >
              {idx + 1} / {images.length}
            </Typography>
          </>
        )}
      </Box>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <Box sx={{ display: "flex", gap: 0.75, mt: 1, overflowX: "auto", pb: 0.5 }}>
          {images.map((src, i) => (
            <Box
              key={i}
              component="img"
              src={src}
              alt={t("carWashes.imageAltIndexed", { index: i + 1 })}
              onClick={() => setIdx(i)}
              sx={{
                width: 64, height: 48, objectFit: "cover", borderRadius: 0.5,
                flexShrink: 0, cursor: "pointer",
                opacity: i === idx ? 1 : 0.5,
                border: "2px solid",
                borderColor: i === idx ? "primary.main" : "transparent",
                transition: "opacity 0.15s, border-color 0.15s",
                "&:hover": { opacity: 0.85 },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────
const ChangeReviewDiffModal = ({ open, onClose, review, onAccept, onReject }: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);

  const entries = React.useMemo(
    () => flattenChanges(review?.changes as Record<string, any>),
    [review]
  );

  // Either a bare array (creation) or {old, new} (an edit).
  const imagesValue = review?.changes?.images as
    | string[]
    | { old: string[]; new: string[] }
    | undefined;

  const hasImages = Array.isArray(imagesValue)
    ? imagesValue.length > 0
    : Boolean(imagesValue?.old?.length || imagesValue?.new?.length);

  const hasDiff = entries.some((e) => e.current !== null);

  const handleAccept = async () => {
    setBusy(true);
    try { await onAccept(); onClose(); } finally { setBusy(false); }
  };

  const handleReject = async () => {
    setBusy(true);
    try { await onReject(); onClose(); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("manageCarWashes.proposedChanges")}</DialogTitle>
      <DialogContent dividers>
        {entries.length === 0 && !hasImages ? (
          <Typography color="text.secondary">
            {t("manageCarWashes.noChangesToDisplay")}
          </Typography>
        ) : (
          <>
            {entries.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("manageCarWashes.colField")}</TableCell>
                    {hasDiff && <TableCell>{t("manageCarWashes.colCurrent")}</TableCell>}
                    <TableCell>{t("manageCarWashes.colProposed")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.field}>
                      <TableCell>{formatFieldLabel(e.field, t)}</TableCell>
                      {hasDiff && (
                        // Old value: red, struck through. A field with no
                        // previous value (a creation) gets a neutral dash
                        // instead — a struck-through one would read as though
                        // the dash itself had been deleted.
                        <TableCell
                          sx={
                            e.current === null
                              ? { color: "text.disabled" }
                              : {
                                  color: "error.main",
                                  textDecoration: "line-through",
                                  fontWeight: 500,
                                }
                          }
                        >
                          {e.current === null ? "—" : e.current}
                        </TableCell>
                      )}
                      {/* New value: green, bold — the same contract as
                          FieldDiff, so both modals read identically. */}
                      <TableCell sx={{ color: "success.main", fontWeight: 600 }}>
                        {e.proposed}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* A creation request has no "before", so the full-size slider is
                still the best way to look at the proposed photos. An EDIT
                records {old, new}, and there the question is which photos
                changed — so it gets the colour-coded gallery instead. */}
            {Array.isArray(imagesValue) ? (
              <ImageSlider images={imagesValue} />
            ) : (
              <Box mt={2}>
                <ImageDiff images={imagesValue} />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common.close")}
        </Button>
        <Button variant="outlined" color="error" disabled={busy} onClick={handleReject}>
          {t("common.reject")}
        </Button>
        <Button variant="contained" color="success" disabled={busy} onClick={handleAccept}>
          {t("common.approve")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeReviewDiffModal;
